import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Alert, Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { AutoComplete, FieldControl } from "../../../shared/components";
import { useAddPortfolio } from "../hooks/useAddPortfolio";
import { useNavigation } from "../../../hooks/useNavigation";

export const AddPortfolio: React.FC<{ isEdit: boolean; }> = ({ isEdit }) => {

  const { goBack, goToAddProject } = useNavigation();

  const { portfolioId } = useParams();

  const {
    formData, fieldErrors, isSubmitting, submitMessage, isLoading, isError, error,
    handleFieldChange, handleSubmit
  } = useAddPortfolio({ isEdit, portfolioId });

  const [countdown, setCountdown] = useState<number>(0);

  useEffect(() => {
    if (submitMessage) {
      setCountdown(5);

      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timeout = setTimeout(() => {
        if (isEdit) {
          goBack();
        } else {
          if (submitMessage.programme && submitMessage.portfolio && submitMessage.portfolio_name) {
            goToAddProject({ programme: String(submitMessage.programme), portfolio: String(submitMessage.portfolio), portfolio_name: String(submitMessage.portfolio_name) });
          } else {
            goBack();
          }
        }
      }, 5000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      }
    }
  }, [submitMessage]);

  if (submitMessage) {
    return (
      <Box m={2} display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Alert
          severity={submitMessage.type}
          sx={{ mb: 3 }}
        >
          {submitMessage.message} <br />
          Redirecting in {countdown} seconds...
        </Alert>
      </Box>
    );
  };

  if (isEdit && isLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    );
  };

  return (
    <Box
      p={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "90dvh",
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          <Typography variant="body1" color="text.primary">Portfolio information</Typography>
          <Typography variant="body2" color="text.secondary">Define the core portfolio details and identifiers</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <AutoComplete
                value={formData.programme}
                onSelect={(value) => handleFieldChange("programme", value)}
                name="programme"
                label="Programme"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FieldControl
                variant="input"
                keyName="portfolio_name"
                label="Portfolio Name"
                value={formData.portfolio_name}
                onChange={handleFieldChange}
                error={!!fieldErrors.portfolio_name}
                helperText={fieldErrors.portfolio_name}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FieldControl
                variant="outlined"
                keyName="description"
                label="Description"
                value={formData.description}
                onChange={handleFieldChange}
                error={!!fieldErrors.description}
                helperText={fieldErrors.description}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldControl
                  variant="date"
                  keyName="start_date"
                  label="Start Date"
                  value={formData.start_date}
                  onChange={handleFieldChange}
                  error={!!fieldErrors.start_date}
                  helperText={fieldErrors.start_date}
                  maxDate={formData.end_date}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldControl
                  variant="date"
                  keyName="end_date"
                  label="End Date"
                  value={formData.end_date}
                  onChange={handleFieldChange}
                  error={!!fieldErrors.end_date}
                  helperText={fieldErrors.end_date}
                  minDate={formData.start_date}
                  required
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1.5} justifyContent={"end"}>
        <Button
          variant="outlined"
          color="primary"
          disabled={isSubmitting}
          onClick={() => {
            goBack();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? `${isEdit ? "Editing" : "Creating"} Portfolio...`
            : isEdit
              ? "Edit Portfolio"
              : "Create Portfolio"}
        </Button>
      </Grid>
    </Box>
  );
};
